import React, { createContext, useContext } from 'react';
import { Card, GameState, PlayerRoundResult } from '../types';

export interface GameBoardContextProps {
  gameState: GameState;
  selectedCard: Card | null;
  hoveredCardId: string | null;
  possibleCaptures: Card[][];
  capturingCards: { cards: Card[]; playedCard: Card; playerIndex: number } | null;
  showRules: boolean;
  showCapturesMenu: boolean;
  scopaParticleSeeds: { x: number; y: number; scale: number; rotate: number; duration: number }[];
  errorMessage: string | null;
  roundStats: {
    pScore: number;
    aiScore: number;
    pResults: PlayerRoundResult | null;
    aiResults: PlayerRoundResult | null;
    results: PlayerRoundResult[];
  } | null;
  dealId: number;
  revealedCardIds: Set<string>;
  revealingCardIds: Set<string>;
  viewportWidth: number;
  onCardSelect: (card: Card) => void;
  onCaptureSelect: (capture: Card[]) => void;
  onHoverCard: (id: string | null) => void;
  onToggleRules: () => void;
  onDismissCapturesMenu: () => void;
  onNextRound: () => void;
  onRestart: () => void;
  onDeclareLowHand: () => void;
  onDeclareThreeAces: () => void;
  playerName?: string;
  isOnline?: boolean;
  isConnected?: boolean;
  onlineOverlay?: React.ReactNode;
  targetScore?: number;
}

const GameBoardContext = createContext<GameBoardContextProps | null>(null);

export function useGameBoard() {
  const context = useContext(GameBoardContext);
  if (!context) {
    throw new Error('useGameBoard must be used within a GameBoardProvider');
  }
  return context;
}

interface GameBoardProviderProps extends GameBoardContextProps {
  children: React.ReactNode;
}

export const GameBoardProvider: React.FC<GameBoardProviderProps> = ({ children, ...props }) => {
  return (
    <GameBoardContext.Provider value={props}>
      {children}
    </GameBoardContext.Provider>
  );
};
